import React from "react";
import {
  Phone, MapPin, FileText, ListChecks, Fingerprint, AlertTriangle, FileCheck2,
  Map, CalendarClock, ArrowDown, CheckCircle2, CalendarDays, Clock, Building,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { Eyebrow, BookButton, RouteButton } from "@/components/Bits";

const questions = [
  "What still depends on you personally?",
  "Who holds the key unwritten knowledge?",
  "Which legacy customer relationships are bound strictly to your presence?",
  "What would be unclear or alarming to a professional buyer?",
];

const steps = [
  { n: "01", title: "The Discovery Call", meta: "30 Minutes", body: "A brief, confidential call to review your timelines, high-level goals, and confirm fit. If aligned, we book your afternoon intensive.", icon: Phone },
  { n: "02", title: "In-Person Strategy Intensive", meta: "1 Afternoon", body: "We come directly to your office. Together we run a structured, interactive diagnostic to pull your operational structure and dependency risks out of your head and onto paper.", icon: MapPin },
  { n: "03", title: "Synthesis & Delivery", meta: "2 Weeks", body: "We take the data from our session back to our office to analyze, structure, and refine your custom transition strategy.", icon: FileText },
];

const deliverables = [
  { icon: ListChecks, t: "Succession Options Intake", b: "A clear analysis of your realistic transition paths — external sale, family transition, EOT, or hiring an operator while you retain equity." },
  { icon: Fingerprint, t: "Founder Dependency Snapshot", b: "A comprehensive audit of exactly where the business relies too heavily on your daily presence — so you can see what a buyer views as risk." },
  { icon: AlertTriangle, t: "Transferability Risk Review", b: "An objective look at the operational blind spots that threaten continuity and exit options, from weak middle management to missing documentation." },
  { icon: FileCheck2, t: "Document & Advisor Checklist", b: "We map exactly what paperwork to compile — corporate structures, financials, shareholder agreements, key contracts — before you pay for billable hours." },
  { icon: Map, t: "Succession Pathway Map", b: "A realistic visual mapping of your best transition paths based on your operational reality, detailing what each path requires and which specialists you’ll need next." },
  { icon: CalendarClock, t: "The 90-Day Action Plan", b: "A practical, step-by-step checklist outlining what to do first, what to fix immediately, and how to approach your next advisor conversation with confidence." },
];

const whoFor = [
  "You know you need a succession plan but have been paralyzed by where to start.",
  "You want to clean up your internal operations before paying high professional advisor fees.",
  "You want to look objectively at what a professional buyer will see before you pitch your story.",
  "You firmly believe your business holds massive value your current financials fail to capture.",
];

const programDetails = [
  { icon: CalendarDays, label: "Total Timeline", value: "2 Weeks" },
  { icon: Clock, label: "Your Time Investment", value: "30 Minute call + 1 Afternoon session" },
  { icon: Building, label: "Location", value: "In-Person at your place of business" },
];

const OwnersPage = () => {
  return (
    <main data-testid="owners-page" className="pt-20">
      {/* HERO */}
      <section className="bg-cream py-20 md:py-28 relative">
        <div className="grid-lines" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <div className="max-w-3xl">
            <Eyebrow>For Business Owners · Sell-Side</Eyebrow>
            <h1 className="font-serif text-4xl md:text-[3.4rem] leading-[1.1] text-ink-deep">
              No succession plan yet? Start by understanding what has to survive <span className="italic text-brand">without you.</span>
            </h1>
            <p className="mt-7 text-lg text-[#475569]">
              You’ve spent decades building something real — your people, your relationships, your reputation, and
              the institutional knowledge locked inside your team. These are genuine assets with real economic
              value. The problem? They don’t show up neatly on a financial statement.
            </p>
            <p className="mt-4 text-base text-[#64748B]">
              We help you identify these vulnerabilities through a high-touch, face-to-face framework. No endless
              homework or automated questionnaires — just a dedicated afternoon at your office to extract your
              operational blueprint and turn it into a concrete strategy.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <BookButton label="Book Your Free 30 Minute Discovery Call" variant="dark" testId="owners-hero-cta" />
              <a href="#deliverables" className="group inline-flex items-center justify-center gap-2 border border-[#CBD5E1] text-ink px-7 py-3.5 text-sm font-medium hover:border-ink hover:bg-white transition-colors">
                See the 6 Key Deliverables <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS CULTURAL CAPITAL */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>What is Cultural Capital?</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep leading-tight">
              The most valuable assets are the ones missing from your balance sheet.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <p className="text-lg text-[#475569] mb-6">
              Cultural Capital is everything your business has built that cannot be calculated by traditional
              accounting — the trust, collective knowledge, customer goodwill, daily habits, reputation, and
              authority structures that keep your company moving.
            </p>
            <blockquote className="border-l-2 border-brand pl-6 py-1 font-serif text-xl md:text-2xl text-ink-deep italic">
              If you do not proactively define what makes your business transferable, a buyer will define what
              makes it risky.
            </blockquote>
          </div>
        </div>
      </section>

      {/* BEFORE THE PAPERWORK */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-12">
            <Eyebrow>Before The Paperwork</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">Succession planning starts long before the paperwork.</h2>
            <p className="mt-4 text-[#475569]">
              Lawyers, accountants, and valuations are vital execution steps — but first you need a crystal-clear
              operational baseline of the business itself:
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {questions.map((q, i) => (
              <Reveal key={i} delay={i * 80} className="flex items-start gap-4 bg-white border border-[#E2E8F0] p-6">
                <span className="font-serif text-3xl text-brand leading-none">?</span>
                <p className="text-[#334155] text-lg">{q}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS — ZERO HOMEWORK TIMELINE */}
      <section data-testid="process-section" className="bg-ink-deep py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <Eyebrow light>Process Overview</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-white">Zero homework. How the Succession Sprint works.</h2>
            <p className="mt-4 text-[#94A3B8]">
              Busy founders don’t have time for massive consulting projects. This sprint extracts the maximum
              intelligence with the absolute minimum of your time.
            </p>
          </div>
          <div className="relative grid md:grid-cols-3 gap-6">
            {/* connecting line */}
            <div className="hidden md:block absolute top-[2.75rem] left-[16%] right-[16%] h-px bg-[#334155]" />
            {steps.map((s, i) => (
              <Reveal key={i} delay={i * 130} className="relative bg-[#162236] border border-[#1E293B] p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-brand text-white font-serif text-lg">{s.n}</span>
                  <s.icon className="w-6 h-6 text-brand-light" />
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-brand-light mb-2">{s.meta}</p>
                <h3 className="font-serif text-xl text-white mb-3">{s.title}</h3>
                <p className="text-[#CBD5E1] text-sm">{s.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6 KEY DELIVERABLES */}
      <section id="deliverables" data-testid="deliverables-section" className="bg-white py-20 md:py-28 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <Eyebrow>The Deliverables</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">The 6 key deliverables you walk away with.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {deliverables.map((d, i) => (
              <Reveal key={i} delay={(i % 3) * 90} className="group border border-[#E2E8F0] p-8 hover:border-brand hover:shadow-lg transition-all bg-cream">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-11 h-11 flex items-center justify-center bg-white border border-[#E2E8F0] group-hover:border-brand transition-colors">
                    <d.icon className="w-5 h-5 text-brand" />
                  </span>
                  <span className="font-serif text-2xl text-[#CBD5E1]">0{i + 1}</span>
                </div>
                <h3 className="font-serif text-xl text-ink-deep mb-3">{d.t}</h3>
                <p className="text-[#475569] text-sm">{d.b}</p>
              </Reveal>
            ))}
          </div>
          {/* Final deliverable callout */}
          <Reveal className="mt-10 bg-ink-deep p-9 md:p-12">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <Eyebrow light>The Final Deliverable</Eyebrow>
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-3">Your Advisor-Ready Succession Roadmap</h3>
                <p className="text-[#94A3B8]">
                  At the close of the two-week sprint you receive your complete Succession Roadmap alongside a
                  One-Page Executive Summary — designed to be shared directly with your accountant, lawyer, valuator,
                  broker, or family stakeholders to streamline planning and protect your equity.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <BookButton label="Book Your Discovery Call" variant="light" testId="deliverables-cta" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Who This Is For</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">Owner-led Canadian businesses facing an inevitable transition.</h2>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {whoFor.map((w, i) => (
              <Reveal key={i} delay={i * 70} className="flex items-start gap-4 bg-white border border-[#E2E8F0] p-5">
                <CheckCircle2 className="w-6 h-6 text-brand shrink-0" />
                <p className="text-[#334155]">{w}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAM DETAILS / PRICING */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-12">
            <Eyebrow>Fixed-Fee. Conflict-Free. Confidential.</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">A transparent, time-boxed engagement.</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {programDetails.map((p, i) => (
              <Reveal key={i} delay={i * 80} className="bg-white border border-[#E2E8F0] p-7">
                <p.icon className="w-7 h-7 text-brand mb-5" />
                <p className="text-xs uppercase tracking-[0.18em] text-[#64748B] mb-2">{p.label}</p>
                <p className="font-serif text-xl text-ink-deep">{p.value}</p>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-sm text-[#64748B] max-w-3xl">
            This is not a sales process, and we are not a business brokerage. We do not value companies, list
            businesses for sale, draft legal documents, provide tax structures, or accept transaction commissions.
            Cultural Capital acts strictly as your independent, objective diagnostic guide.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink-deep py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight">Can the value you built survive without you?</h2>
          <p className="mt-6 text-lg text-[#94A3B8]">
            If your business is still deeply dependent on you, that’s not a failure — it just means the value you
            built is currently trapped. Let’s get it out of your head and make it visible, organized, and transferable.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <BookButton label="Schedule Your Free 30 Minute Discovery Call" variant="light" testId="owners-final-cta" />
            <RouteButton to="/contact" label="Contact Us" variant="outlineLight" />
          </div>
          <p className="mt-6 text-sm text-[#64748B]">Serving Toronto, the GTA, and Southwestern Ontario.</p>
        </div>
      </section>
    </main>
  );
};

export default OwnersPage;
